import React, { useState, useEffect } from 'react';
import LogbookView from './LogbookView';
import LogEntryForm from './LogEntryForm';
import SpeciesEditForm from './SpeciesEditForm';
import usePlants from '../hooks/usePlants';
import './PlantDetailView.css';

/**
 * Display a field value with its data source annotation.
 */
function FieldWithSource({ label, value, field, sources }) {
  const sourceInfo = sources?.[field];
  return (
    <p className="plant-detail-view__field-with-source">
      {label}: {value}
      {sourceInfo && (
        <span className="plant-detail-view__source">
          (Source: {sourceInfo.source}
          {sourceInfo.rawValue !== undefined && sourceInfo.rawValue !== null && ` · raw: ${sourceInfo.rawValue}`}
          {sourceInfo.unit && ` ${sourceInfo.unit}`}
          {sourceInfo.derived && ' · derived'}
          )
        </span>
      )}
    </p>
  );
}

/**
 * Instance detail page — shows one specific plant instance's location, quantities, and logbook.
 */
function InstanceDetailView({ instance, onBack, onEdit, onEditSpecies, getLogEntries, addLogEntry, getPhotos, updatePlant, speciesData: speciesDataProp }) {
  const [logEntries, setLogEntries] = useState([]);
  const [showLogForm, setShowLogForm] = useState(false);
  const { species: allSpeciesForInstance } = usePlants();

  // Look up species data directly
  const speciesData = speciesDataProp || (instance ? allSpeciesForInstance.find(s =>
    s.scientificName?.toLowerCase() === instance.scientificName?.toLowerCase() ||
    s.commonName?.toLowerCase() === instance.commonName?.toLowerCase()
  ) : null) || null;

  // Debug: remove after confirming
  console.log('[InstanceDetailView] species count:', allSpeciesForInstance.length, 'instance:', instance?.commonName, 'found species:', speciesData?.commonName, 'ratings:', speciesData?.agcpRating, speciesData?.empRating);

  useEffect(() => {
    async function load() {
      if (instance && getLogEntries) {
        const entries = await getLogEntries(instance.id);
        setLogEntries(entries);
      }
    }
    load();
  }, [instance, getLogEntries]);

  const handleLogSubmit = async ({ notes, photos, quantities }) => {
    if (quantities && updatePlant) {
      await updatePlant(instance.id, quantities);
    }
    await addLogEntry(instance.id, notes, photos);
    const entries = await getLogEntries(instance.id);
    setLogEntries(entries);
    setShowLogForm(false);
  };

  if (!instance) return null;

  if (showLogForm) {
    return (
      <LogEntryForm
        onSubmit={handleLogSubmit}
        onCancel={() => setShowLogForm(false)}
        currentQuantities={{
          qtyPlugs: instance.qtyPlugs ?? 0,
          qtySoloCups: instance.qtySoloCups ?? 0,
          qtyQuarts: instance.qtyQuarts ?? 0,
          qtyGallons: instance.qtyGallons ?? 0,
        }}
      />
    );
  }

  const totalQty = (instance.qtyPlugs || 0) + (instance.qtySoloCups || 0) + (instance.qtyQuarts || 0) + (instance.qtyGallons || 0);

  return (
    <div className="plant-detail-view">
      <div className="plant-detail-view__header">
        <button className="plant-detail-view__back-btn" onClick={onBack}>
          ← Back to species
        </button>
        <div className="plant-detail-view__header-actions">
          <button className="plant-detail-view__edit-btn" onClick={() => onEdit(instance.id)}>
            Edit Instance
          </button>
          {onEditSpecies && (
            <button className="plant-detail-view__edit-btn plant-detail-view__edit-btn--secondary" onClick={onEditSpecies}>
              Edit Species
            </button>
          )}
        </div>
      </div>

      <h2 className="plant-detail-view__name">{instance.commonName}</h2>
      <p className="plant-detail-view__scientific">{instance.scientificName}</p>

      <div className="plant-detail-view__section">
        <h3>Location</h3>
        <p>Row {instance.gridRow}, Column {instance.gridCol}</p>
        {instance.trayNumber && <p>Tray: {instance.trayNumber}</p>}
        {instance.source && <p>Source: {instance.source}</p>}
      </div>

      <div className="plant-detail-view__section">
        <h3>Quantities {totalQty === 0 && <span className="plant-detail-view__empty-tag">(Empty)</span>}</h3>
        <div className="plant-detail-view__quantities">
          <span>Plugs: {instance.qtyPlugs || 0}</span>
          <span>Solo Cups: {instance.qtySoloCups || 0}</span>
          <span>Quarts: {instance.qtyQuarts || 0}</span>
          <span>Gallons: {instance.qtyGallons || 0}</span>
        </div>
      </div>

      {(() => {
        const sp = speciesData || instance;
        const hasRatings = sp.agcpRating || sp.empRating;
        const hasConditions = sp.lightRequirements || sp.moistureRequirements || sp.soilRequirements;
        const hasBloom = sp.bloomSeason || sp.bloomColor;
        return (
          <>
            {hasRatings && (
              <div className="plant-detail-view__section">
                <h3>Wetland Ratings</h3>
                {sp.agcpRating && <p>AGCP: {sp.agcpRating}</p>}
                {sp.empRating && <p>EMP: {sp.empRating}</p>}
              </div>
            )}
            {hasConditions && (
              <div className="plant-detail-view__section">
                <h3>Growing Conditions</h3>
                {sp.lightRequirements && <p>Light: {sp.lightRequirements}</p>}
                {sp.moistureRequirements && <p>Moisture: {sp.moistureRequirements}</p>}
                {sp.soilRequirements && <p>Soil: {sp.soilRequirements}</p>}
              </div>
            )}
            {hasBloom && (
              <div className="plant-detail-view__section">
                <h3>Bloom</h3>
                {sp.bloomSeason && <p>Season: {sp.bloomSeason}</p>}
                {sp.bloomColor && <p>Color: {sp.bloomColor}</p>}
              </div>
            )}
            <div className="plant-detail-view__section">
              <h3>Salt Tolerance</h3>
              {sp.saltTolerance
                ? <p>{'●'.repeat(sp.saltTolerance)}{'○'.repeat(5 - sp.saltTolerance)} ({sp.saltTolerance}/5)</p>
                : <p>Not set</p>
              }
            </div>
          </>
        );
      })()}

      <div className="plant-detail-view__logbook">
        <LogbookView
          entries={logEntries}
          onAddEntry={() => setShowLogForm(true)}
          plantName={`${instance.commonName} (Row ${instance.gridRow}, Col ${instance.gridCol})`}
          getPhotos={getPhotos}
        />
      </div>
    </div>
  );
}

/**
 * Species overview — shows shared species info + list of all instances.
 * Clicking an instance navigates to InstanceDetailView.
 */
function PlantDetailView({ plant, onBack, onEdit, getLogEntries, addLogEntry, getPhotos, updatePlant, allInstances, updateSpecies, speciesData: speciesDataProp }) {
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [editingSpecies, setEditingSpecies] = useState(false);
  const { species: allSpecies } = usePlants();

  const instances = allInstances && allInstances.length > 0 ? allInstances : (plant ? [plant] : []);

  // Look up species data directly from context (most reliable)
  const speciesData = speciesDataProp || (plant ? allSpecies.find(s =>
    s.scientificName?.toLowerCase() === plant.scientificName?.toLowerCase() ||
    s.commonName?.toLowerCase() === plant.commonName?.toLowerCase()
  ) : null) || null;

  // Use species data for display (falls back to plant instance data)
  const displayData = {
    ...plant,
    lightRequirements: speciesData?.lightRequirements || plant?.lightRequirements || '',
    moistureRequirements: speciesData?.moistureRequirements || plant?.moistureRequirements || '',
    soilRequirements: speciesData?.soilRequirements || plant?.soilRequirements || '',
    bloomSeason: speciesData?.bloomSeason || plant?.bloomSeason || '',
    bloomColor: speciesData?.bloomColor || plant?.bloomColor || '',
    agcpRating: speciesData?.agcpRating || plant?.agcpRating || null,
    empRating: speciesData?.empRating || plant?.empRating || null,
    saltTolerance: speciesData?.saltTolerance || plant?.saltTolerance || null,
    rootType: speciesData?.rootType || '',
    rootDepth: speciesData?.rootDepth || '',
    spreadRate: speciesData?.spreadRate || '',
    groundCoverDensity: speciesData?.groundCoverDensity || '',
    floodTolerance: speciesData?.floodTolerance || '',
    tidalTolerance: speciesData?.tidalTolerance || '',
    droughtTolerance: speciesData?.droughtTolerance || '',
    applications: speciesData?.applications || [],
    zonePosition: speciesData?.zonePosition || '',
  };

  // Debug
  console.log('[PlantDetailView] plant:', plant?.commonName, 'speciesData:', speciesData?.commonName, 'allSpecies count:', allSpecies.length, 'displayData.agcpRating:', displayData.agcpRating, 'displayData.lightRequirements:', displayData.lightRequirements);

  // If editing species-level data
  if (editingSpecies) {
    return (
      <SpeciesEditForm
        species={displayData}
        onSubmit={async (data) => {
          if (updateSpecies) {
            await updateSpecies(plant.speciesId || plant.id, data);
          }
          // Also update all instances with the new species-level fields
          if (updatePlant && instances) {
            for (const inst of instances) {
              await updatePlant(inst.id, {
                commonName: data.commonName,
                scientificName: data.scientificName,
                agcpRating: data.agcpRating,
                empRating: data.empRating,
                lightRequirements: data.lightRequirements,
                moistureRequirements: data.moistureRequirements,
                soilRequirements: data.soilRequirements,
                bloomSeason: data.bloomSeason,
                bloomColor: data.bloomColor,
                saltTolerance: data.saltTolerance,
              });
            }
          }
          setEditingSpecies(false);
        }}
        onCancel={() => setEditingSpecies(false)}
      />
    );
  }

  // If viewing a specific instance
  if (selectedInstance) {
    return (
      <InstanceDetailView
        instance={selectedInstance}
        onBack={() => setSelectedInstance(null)}
        onEdit={onEdit}
        onEditSpecies={() => setEditingSpecies(true)}
        getLogEntries={getLogEntries}
        addLogEntry={addLogEntry}
        getPhotos={getPhotos}
        updatePlant={updatePlant}
        speciesData={speciesData}
      />
    );
  }

  // Species overview with instance list
  return (
    <div className="plant-detail-view">
      <div className="plant-detail-view__header">
        <button className="plant-detail-view__back-btn" onClick={onBack}>
          ← Back
        </button>
        <button className="plant-detail-view__edit-btn" onClick={() => setEditingSpecies(true)}>
          Edit Species
        </button>
      </div>

      <h2 className="plant-detail-view__name">{plant.commonName}</h2>
      <p className="plant-detail-view__scientific">{plant.scientificName}</p>

      {(displayData.agcpRating || displayData.empRating) && (
        <div className="plant-detail-view__section">
          <h3>Wetland Ratings</h3>
          {displayData.agcpRating && <p>AGCP: {displayData.agcpRating}</p>}
          {displayData.empRating && <p>EMP: {displayData.empRating}</p>}
        </div>
      )}

      {(displayData.lightRequirements || displayData.moistureRequirements || displayData.soilRequirements) && (
        <div className="plant-detail-view__section">
          <h3>Growing Conditions</h3>
          {displayData.lightRequirements && <p>Light: {displayData.lightRequirements}</p>}
          {displayData.moistureRequirements && <p>Moisture: {displayData.moistureRequirements}</p>}
          {displayData.soilRequirements && <p>Soil: {displayData.soilRequirements}</p>}
        </div>
      )}

      {(displayData.bloomSeason || displayData.bloomColor) && (
        <div className="plant-detail-view__section">
          <h3>Bloom</h3>
          {displayData.bloomSeason && <p>Season: {displayData.bloomSeason}</p>}
          {displayData.bloomColor && <p>Color: {displayData.bloomColor}</p>}
        </div>
      )}

      <div className="plant-detail-view__section">
        <h3>Salt Tolerance</h3>
        {displayData.saltTolerance
          ? <p className="plant-detail-view__field-with-source">
              {'●'.repeat(displayData.saltTolerance)}{'○'.repeat(5 - displayData.saltTolerance)} ({displayData.saltTolerance}/5)
              {speciesData?._sources?.saltTolerance && (
                <span className="plant-detail-view__source">
                  (Source: {speciesData._sources.saltTolerance.source})
                </span>
              )}
            </p>
          : <p>Not set</p>
        }
      </div>

      {(displayData.rootType || displayData.rootDepth || displayData.spreadRate || displayData.groundCoverDensity) && (
        <div className="plant-detail-view__section">
          <h3>Erosion & Stabilization</h3>
          {displayData.rootType && <FieldWithSource label="Root Type" value={displayData.rootType} field="rootType" sources={speciesData?._sources} />}
          {displayData.rootDepth && <FieldWithSource label="Root Depth" value={displayData.rootDepth} field="rootDepth" sources={speciesData?._sources} />}
          {displayData.spreadRate && <FieldWithSource label="Spread Rate" value={displayData.spreadRate} field="spreadRate" sources={speciesData?._sources} />}
          {displayData.groundCoverDensity && <FieldWithSource label="Ground Cover" value={displayData.groundCoverDensity} field="groundCoverDensity" sources={speciesData?._sources} />}
        </div>
      )}

      {(displayData.floodTolerance || displayData.tidalTolerance || displayData.droughtTolerance) && (
        <div className="plant-detail-view__section">
          <h3>Water Tolerance</h3>
          {displayData.floodTolerance && <FieldWithSource label="Flood" value={displayData.floodTolerance} field="floodTolerance" sources={speciesData?._sources} />}
          {displayData.tidalTolerance && <FieldWithSource label="Tidal" value={displayData.tidalTolerance} field="tidalTolerance" sources={speciesData?._sources} />}
          {displayData.droughtTolerance && <FieldWithSource label="Drought" value={displayData.droughtTolerance} field="droughtTolerance" sources={speciesData?._sources} />}
        </div>
      )}

      {displayData.zonePosition && (
        <div className="plant-detail-view__section">
          <h3>Zone Position</h3>
          <p>{displayData.zonePosition}</p>
        </div>
      )}

      {displayData.applications?.length > 0 && (
        <div className="plant-detail-view__section">
          <h3>Applications</h3>
          <div className="plant-detail-view__apps">
            {displayData.applications.map(a => <span key={a} className="plant-detail-view__app-tag">{a}</span>)}
          </div>
        </div>
      )}

      <BackfillButton speciesData={speciesData} displayData={displayData} />

      <div className="plant-detail-view__section">
        <h3>Instances ({instances.length})</h3>
        <ul className="plant-detail-view__instances">
          {instances.map((inst) => {
            const totalQty = (inst.qtyPlugs || 0) + (inst.qtySoloCups || 0) + (inst.qtyQuarts || 0) + (inst.qtyGallons || 0);
            const isActive = totalQty > 0;
            return (
              <li
                key={inst.id}
                className={`plant-detail-view__instance ${!isActive ? 'plant-detail-view__instance--inactive' : ''}`}
                onClick={() => setSelectedInstance(inst)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') setSelectedInstance(inst); }}
              >
                <div className="plant-detail-view__instance-info">
                  <span className="plant-detail-view__instance-location">
                    Row {inst.gridRow}, Col {inst.gridCol}
                  </span>
                  {inst.trayNumber && <span> · Tray: {inst.trayNumber}</span>}
                  {inst.source && <span> · {inst.source}</span>}
                </div>
                <div className="plant-detail-view__instance-right">
                  <span className={`plant-detail-view__instance-qty ${!isActive ? 'plant-detail-view__instance-qty--zero' : ''}`}>
                    {isActive ? `${totalQty} plants` : 'Empty'}
                  </span>
                  <span className="plant-detail-view__instance-arrow">→</span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function BackfillButton({ speciesData, displayData }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const { loadPlants } = usePlants();

  // Check if data is missing
  const missingTrefle = !displayData.lightRequirements || !displayData.moistureRequirements || !displayData.soilRequirements || !displayData.bloomSeason || !displayData.bloomColor || !displayData.rootDepth || !displayData.spreadRate || !displayData.droughtTolerance;
  const missingNwpl = !displayData.agcpRating || !displayData.empRating;
  const missingFloodTolerance = !displayData.floodTolerance;
  const missingUsda = !displayData.family || !displayData.spreadRate || !displayData.saltTolerance || !displayData.rootDepth || !displayData.droughtTolerance;
  const missingOther = !displayData.rootType || !displayData.groundCoverDensity || !displayData.tidalTolerance || !displayData.zonePosition || !(displayData.applications?.length > 0);

  if (!missingTrefle && !missingNwpl && !missingFloodTolerance && !missingUsda && !missingOther) return null;
  if (!speciesData?.id) return null;

  async function handleBackfill() {
    setRunning(true);
    setResult(null);
    try {
      const { sharedDb, recordChange } = await import('@monorepo/shared');
      const { lookupSpecies, deriveFloodToleranceFromNWPL } = await import('../services/trefleService');
      const { lookupWetlandRating } = await import('../services/wetlandBackfill');

      const species = await sharedDb.plantSpecies.get(speciesData.id);
      if (!species) { setResult('Species not found in database'); setRunning(false); return; }

      const updates = {};

      // Trefle backfill — token is stored in sharedDb.syncConfig
      const config = await sharedDb.syncConfig.get('sync-config');
      const trefleToken = config?.trefleToken || '';
      const sources = { ...(species._sources || {}) };

      console.log('[Backfill] Species:', species.scientificName, 'Trefle token:', trefleToken ? 'set' : 'NOT SET');
      if (trefleToken && missingTrefle) {
        const data = await lookupSpecies(species.scientificName, trefleToken);
        console.log('[Backfill] Trefle result:', data);
        if (data) {
          if (!species.lightRequirements && data.light) {
            updates.lightRequirements = data.light;
            sources.lightRequirements = { source: 'Trefle', rawValue: data._raw?.light, unit: '0-10 scale' };
          }
          if (!species.moistureRequirements && data.moisture) {
            updates.moistureRequirements = data.moisture;
            sources.moistureRequirements = { source: 'Trefle', rawValue: data._raw?.atmospheric_humidity };
          }
          if (!species.soilRequirements && data.soil) {
            updates.soilRequirements = data.soil;
            sources.soilRequirements = { source: 'Trefle', rawValue: `texture: ${data._raw?.soil_texture}, nutriments: ${data._raw?.soil_nutriments}` };
          }
          if (!species.bloomSeason && data.bloomMonths?.length > 0) {
            const seasonMap = { jan: 'Winter', feb: 'Winter', mar: 'Spring', apr: 'Spring', may: 'Spring', jun: 'Summer', jul: 'Summer', aug: 'Summer', sep: 'Fall', oct: 'Fall', nov: 'Fall', dec: 'Winter' };
            updates.bloomSeason = [...new Set(data.bloomMonths.map(m => seasonMap[m.toLowerCase()] || ''))].filter(Boolean).join(' to ');
            sources.bloomSeason = { source: 'Trefle', rawValue: data.bloomMonths.join(', ') };
          }
          if (!species.bloomColor && data.flowerColor) {
            updates.bloomColor = data.flowerColor;
            sources.bloomColor = { source: 'Trefle' };
          }
          if (!species.rootDepth && data.rootDepth) {
            updates.rootDepth = data.rootDepth;
            sources.rootDepth = { source: 'Trefle', rawValue: data._raw?.root_depth, unit: 'mm' };
          }
          if (!species.spreadRate && data.spreadRate) {
            updates.spreadRate = data.spreadRate;
            sources.spreadRate = { source: 'Trefle', rawValue: data._raw?.spread, unit: 'cm' };
          }
          if (!species.droughtTolerance && data.droughtTolerance) {
            updates.droughtTolerance = data.droughtTolerance;
            sources.droughtTolerance = { source: 'Trefle', rawValue: data._raw?.drought_tolerance, unit: '0-10 scale' };
          }
        }
      } else if (!trefleToken) {
        console.log('[Backfill] Skipping Trefle — no token. Set it on the Config page under Data Backfill.');
      }

      // NWPL backfill
      if (missingNwpl) {
        const ratings = await lookupWetlandRating(species.scientificName);
        console.log('[Backfill] NWPL result for "' + species.scientificName + '":', ratings);
        if (!species.agcpRating && ratings.agcp) {
          updates.agcpRating = ratings.agcp;
          sources.agcpRating = { source: 'NWPL' };
        }
        if (!species.empRating && ratings.emp) {
          updates.empRating = ratings.emp;
          sources.empRating = { source: 'NWPL' };
        }
      }

      // Derive flood tolerance from NWPL ratings if not already set
      if (missingFloodTolerance) {
        const agcp = updates.agcpRating || species.agcpRating;
        const emp = updates.empRating || species.empRating;
        if (agcp || emp) {
          const derived = deriveFloodToleranceFromNWPL(agcp, emp);
          if (derived) {
            updates.floodTolerance = derived;
            sources.floodTolerance = { source: 'NWPL', rawValue: agcp || emp, derived: true };
          }
        }
      }

      // USDA PLANTS characteristics backfill
      try {
        const { lookupUsdaCharacteristics } = await import('../services/usdaBackfill');
        const usdaChars = await lookupUsdaCharacteristics(species.scientificName);
        if (usdaChars) {
          if (!species.droughtTolerance && !updates.droughtTolerance && usdaChars.droughtTolerance) {
            updates.droughtTolerance = usdaChars.droughtTolerance;
            sources.droughtTolerance = { source: 'USDA PLANTS', rawValue: usdaChars._rawDroughtTolerance || usdaChars.droughtTolerance };
          }
          if (!species.rootDepth && !updates.rootDepth && usdaChars.rootDepth) {
            updates.rootDepth = usdaChars.rootDepth;
            sources.rootDepth = { source: 'USDA PLANTS', rawValue: usdaChars._rawRootDepthInches, unit: 'inches' };
          }
          if (!species.spreadRate && !updates.spreadRate && usdaChars.spreadRate) {
            updates.spreadRate = usdaChars.spreadRate;
            sources.spreadRate = { source: 'USDA PLANTS', rawValue: usdaChars._rawSpreadRate };
          }
          if (!species.floodTolerance && !updates.floodTolerance && usdaChars.floodTolerance) {
            updates.floodTolerance = usdaChars.floodTolerance;
            sources.floodTolerance = { source: 'USDA PLANTS', rawValue: usdaChars._rawAnaerobicTolerance, derived: true };
          }
          if (!species.saltTolerance && !updates.saltTolerance && usdaChars.saltTolerance) {
            updates.saltTolerance = usdaChars.saltTolerance;
            sources.saltTolerance = { source: 'USDA PLANTS', rawValue: usdaChars._rawSalinityTolerance };
          }
          if (!species.family && usdaChars.family) {
            updates.family = usdaChars.family;
            sources.family = { source: 'USDA PLANTS' };
          }
        }
      } catch (err) {
        // USDA data not available — that's fine, skip it
      }

      // Store sources metadata
      if (Object.keys(sources).length > 0) {
        updates._sources = sources;
      }

      console.log('[Backfill] Updates to apply:', updates);

      if (Object.keys(updates).length > 0) {
        const merged = { ...species, ...updates, updatedAt: new Date().toISOString() };
        await sharedDb.plantSpecies.put(merged);
        await recordChange('plantSpecies', species.id, 'put');
        await loadPlants();
        setResult(`Updated ${Object.keys(updates).length} field(s)`);
      } else {
        setResult('No additional data found');
      }
    } catch (err) {
      setResult(`Error: ${err.message}`);
    }
    setRunning(false);
  }

  async function handleGeminiBackfill() {
    setRunning(true);
    setResult(null);
    try {
      const { sharedDb, recordChange } = await import('@monorepo/shared');
      const { lookupWithGemini } = await import('../services/geminiBackfill');

      const species = await sharedDb.plantSpecies.get(speciesData.id);
      if (!species) { setResult('Species not found'); setRunning(false); return; }

      const geminiKey = localStorage.getItem('gemini-api-key') || '';
      if (!geminiKey) { setResult('Set your Gemini API key on the Config page first'); setRunning(false); return; }

      // Determine which fields are missing
      const allFields = ['rootType', 'rootDepth', 'spreadRate', 'groundCoverDensity', 'floodTolerance', 'tidalTolerance', 'droughtTolerance', 'lightRequirements', 'moistureRequirements', 'soilRequirements', 'bloomSeason', 'bloomColor', 'saltTolerance', 'zonePosition'];
      const missing = allFields.filter(f => !species[f]);

      if (missing.length === 0) { setResult('All fields already populated'); setRunning(false); return; }

      const geminiResult = await lookupWithGemini(species.scientificName, missing, geminiKey);
      if (!geminiResult || Object.keys(geminiResult.fields).length === 0) {
        setResult('Gemini could not provide data for this species');
        setRunning(false);
        return;
      }

      const updates = geminiResult.fields;
      const sources = { ...(species._sources || {}) };
      for (const field of Object.keys(updates)) {
        sources[field] = { source: 'Gemini', rawValue: geminiResult.rawResponse ? '(AI-generated)' : undefined };
      }
      updates._sources = sources;

      const merged = { ...species, ...updates, updatedAt: new Date().toISOString() };
      await sharedDb.plantSpecies.put(merged);
      await recordChange('plantSpecies', species.id, 'put');
      await loadPlants();
      setResult(`Gemini filled ${Object.keys(geminiResult.fields).length} field(s)`);
    } catch (err) {
      setResult(`Error: ${err.message}`);
    }
    setRunning(false);
  }

  async function handleGeminiApplications() {
    setRunning(true);
    setResult(null);
    try {
      const { sharedDb, recordChange } = await import('@monorepo/shared');

      const species = await sharedDb.plantSpecies.get(speciesData.id);
      if (!species) { setResult('Species not found'); setRunning(false); return; }

      const geminiKey = localStorage.getItem('gemini-api-key') || '';
      if (!geminiKey) { setResult('Set your Gemini API key on the Config page first'); setRunning(false); return; }

      const GEMINI_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-3.5-flash:generateContent';
      const prompt = `For the plant species "${species.scientificName}" (${species.commonName || ''}), determine which ecological/landscaping applications it is suitable for.

Choose ONLY from this exact list of application tags:
- "stream bank" (stabilizes eroding stream/river banks)
- "rain garden" (handles stormwater in designed rain gardens)
- "slope stabilization" (prevents erosion on hillsides)
- "bioswale" (filters stormwater in linear channels)
- "living shoreline" (protects coastal/lakefront edges)
- "riparian buffer" (planted along waterways to filter runoff)
- "stormwater pond" (grows in or around retention ponds)
- "wetland restoration" (used in constructed or restored wetlands)

Return ONLY a JSON array of matching tags. Be selective — only include tags where this species is genuinely well-suited. Return [] if none apply.

Example response: ["stream bank", "rain garden", "riparian buffer"]`;

      const res = await fetch(`${GEMINI_URL}?key=${geminiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }],
          generationConfig: { temperature: 0.1 },
        }),
      });

      if (!res.ok) {
        setResult(`Gemini API error: ${res.status}`);
        setRunning(false);
        return;
      }

      const data = await res.json();
      const text = (data.candidates?.[0]?.content?.parts?.[0]?.text || '').trim();
      const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
      const tags = JSON.parse(cleaned);

      if (!Array.isArray(tags) || tags.length === 0) {
        setResult('Gemini found no applicable tags for this species');
        setRunning(false);
        return;
      }

      // Merge with existing applications
      const existing = species.applications || [];
      const merged = [...new Set([...existing, ...tags])];
      const sources = { ...(species._sources || {}), applications: { source: 'Gemini' } };

      await sharedDb.plantSpecies.put({
        ...species,
        applications: merged,
        _sources: sources,
        updatedAt: new Date().toISOString(),
      });
      await recordChange('plantSpecies', species.id, 'put');
      await loadPlants();
      setResult(`Added ${tags.length} application tag(s): ${tags.join(', ')}`);
    } catch (err) {
      setResult(`Error: ${err.message}`);
    }
    setRunning(false);
  }

  return (
    <div className="plant-detail-view__section">
      <div className="plant-detail-view__backfill-buttons">
        <button
          className="plant-detail-view__backfill-btn"
          onClick={handleBackfill}
          disabled={running}
        >
          {running ? 'Fetching...' : 'Backfill (Trefle/NWPL/USDA)'}
        </button>
        <button
          className="plant-detail-view__backfill-btn plant-detail-view__backfill-btn--gemini"
          onClick={handleGeminiBackfill}
          disabled={running}
        >
          {running ? 'Fetching...' : 'Ask Gemini'}
        </button>
        <button
          className="plant-detail-view__backfill-btn plant-detail-view__backfill-btn--gemini"
          onClick={handleGeminiApplications}
          disabled={running}
        >
          {running ? 'Fetching...' : 'Suggest Applications'}
        </button>
      </div>
      {result && <p className="plant-detail-view__backfill-result">{result}</p>}
    </div>
  );
}

export default PlantDetailView;
